<!--
* A datetime picker wrapper component
*
* @param {DatetimeTypes} type - Type of datepicker to show
* @param {number} minDatetime - Epoch of minimum datetime
* @param {number} maxDatetime - Epoch of maximum datetime
* @param {string} locale - 2 digit ISO language code
* @param {string} placeholder - Text to use as input placeholder
*
* @fires MsDatetimePicker#input
-->

<!--
 * date has been selected event.
 *
 * @event MsDatetimePicker#input
 * @property {String} The value of the selected date
-->
<template>
  <div>
    <div
      class="ms-datetime-picker">
      <ms-datetime
        v-model="formattedSelectedDate"
        :type="type"
        :placeholder="placeholder"
        class="datetime-picker"
        ref="datetimeComponent"
        input-format="DD-MM-YYYY"
        :i18n="{cancel: $t('message.cancelButton'), ok: $t('message.nextButton')}"
        :moment-locale="this.$store.getters.language.languageIsoCode"
        :min-date="isoMinDatetime"
        :max-date="isoMaxDatetime"
        :auto-continue="true"
        :auto-close="true"/>
      <div
        @click="open"
        class="date-submit">
        <img
          class="datetime-picker-icon"
          src="@/assets/theme/images/datetime-picker-icon.png">
      </div>
    </div>
  </div>
</template>

<script>
  import { DateTime as LuxonDateTime, Settings } from 'luxon'
  import { Datetime } from '@/components/ms-datetime'
  import 'vue-datetime/dist/vue-datetime.css'

  export const DatetimeTypes = {
    DATE: 'date',
    DATETIME: 'datetime',
    YEAR: 'year'
  }

  export default {
    name: 'MsDatetimePicker',
    components: {
      Datetime
    },

    model: {
      prop: 'selectedDate',
      event: 'input'
    },

    props: {
      selectedDate: {
        type: Number
      },
      type: {
        type: String,
        required: true
      },
      minDatetime: {
        type: Number
      },
      maxDatetime: {
        type: Number
      },
      locale: {
        type: String
      },
      placeholder: {
        type: String
      }
    },

    mounted () {
      if (this.locale) {
        Settings.defaultLocale = this.locale
      } else {
        Settings.defaultLocale = 'en-GB'
      }
    },

    methods: {
      /**
       * Open datetime popup
       */
      open: function (event) {
        this.$refs.datetimeComponent.open(event)
      }
    },

    computed: {

      formattedSelectedDate: {
        /**
         * Gets epoch selected date and returns ISO formatted date
         */
        get () {
          if (this.selectedDate !== null) {
            let selectedDate = LuxonDateTime.fromMillis(this.selectedDate)
            return selectedDate ? selectedDate.toISO() : null
          }

          return LuxonDateTime.local().toISO()
        },

        /**
         * Sets selected date to epoch
         * @param value
         */
        set (value) {
          var dateTime = LuxonDateTime.fromISO(value)
          let formattedDateTime

          // If there is a value and it isn't the default 1970 epoch
          // set the value to epoch
          if (value && dateTime.valueOf() !== 0) {
            formattedDateTime = dateTime.valueOf()
          } else {
            // Otherwise set the value to empty string
            formattedDateTime = null
          }

          // Emit input
          this.$emit('input', formattedDateTime)
        }
      },

      /**
       * Gets ISO formatted min datetime
       */
      isoMinDatetime: {
        get () {
          if (this.minDatetime) {
            return LuxonDateTime.fromMillis(this.minDatetime).toISO()
          }
          return null
        }
      },

      /**
       * Gets ISO formatted max datetime
       */
      isoMaxDatetime: {
        get () {
          if (this.maxDatetime) {
            return LuxonDateTime.fromMillis(this.maxDatetime).toISO()
          }
          return null
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "../assets/sass/shared.scss";

  $datepicker-main-color: $primary-brand-color;
  $action-button-font-weight: $semibold-font-weight;
  $action-button-text-color: $primary-text-color;
  $month-day-item-border-radius: 0;
  $month-day-item-active-font-weight: $semibold-font-weight;
  $datetime-picker-button-width: $dims-datetime-picker-input-button-width;
  $datetime-picker-size: $dims-datetime-picker-size;

  .datetime-picker {
    &.vdatetime {
      font-family: $base-font-stack;
      cursor: pointer;
      flex: 1 0 auto;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);


      ::v-deep .vdatetime-overlay {
        position: fixed;
      }

      ::v-deep input {
        background-color: $secondary-brand-color;
        appearance: none;
        border: $default-border-style;
        border-radius: 0;
        box-shadow: none;
        font-size: $medium-font-size;
        height: auto;
        justify-content: flex-start;
        line-height: 1.5em;
        padding: 13px 10px;
        position: relative;
        vertical-align: top;
        color: $primary-text-color;
        width: 100%;
      }

      ::v-deep .vdatetime-slot__available {
        display: none;
      }

      ::v-deep .vdatetime-slot__not-available {
        display: none;
      }
      ::v-deep .vdatetime-popup__header,
      ::v-deep .vdatetime-calendar__month__day--selected > span > span,
      ::v-deep .vdatetime-calendar__month__day--selected:hover > span > span {
        background: $datepicker-main-color;
      }

      ::v-deep .vdatetime-calendar__month__day > span > span {
        border-radius: $month-day-item-border-radius;
      }

      ::v-deep .vdatetime-calendar__month__day--selected > span > span,
      ::v-deep .vdatetime-calendar__month__day--selected:hover > span > span {
        font-weight: $month-day-item-active-font-weight;
      }

      ::v-deep .vdatetime-year-picker__item--selected,
      ::v-deep .vdatetime-time-picker__item--selected {
        color: $datepicker-main-color;
      }

      ::v-deep .vdatetime-popup__actions__button {
        font-weight: $action-button-font-weight;
        color: $action-button-text-color;
      }
    }
  }

  .ms-datetime-picker {
    display: flex;
  }

  .datetime-picker-icon {
    width: $datetime-picker-size;
  }

  .date-submit {
    background-color: $primary-brand-color;
    width: $datetime-picker-button-width;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

</style>
